import React, { useState, useEffect } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { planningService, patientService } from '../../services/api';
import { RendezVous, CreateRendezVousData, Patient, Doctor, PaginatedResponse, CreatePatientData } from '@/types';
import { X, Plus, User } from 'lucide-react';
import PhoneInput from '../common/PhoneInput';
import { validerNumero } from '../../utils/phone';

interface RendezVousFormProps {
  rendezVous?: RendezVous;
  onClose: () => void;
  onSuccess?: () => void;
}

const RendezVousForm: React.FC<RendezVousFormProps> = ({ rendezVous, onClose, onSuccess }) => {
  const queryClient = useQueryClient();
  const location = useLocation();
  const [showNewPatientForm, setShowNewPatientForm] = useState(false);
  const [patientSearchTerm, setPatientSearchTerm] = useState('');
  const [showPatientDropdown, setShowPatientDropdown] = useState(false);
  const [newPatientData, setNewPatientData] = useState({
    nom: '',
    prenoms: '',
    telephone: '',
    genre: 'M' as 'M' | 'F',
    age_input: '' as string | number
  });

  // Récupérer les données de navigation
  const navigationState = location.state as { selectedPatientId?: number; patientInfo?: any } | null;

  // Fonction pour convertir une date GMT en format local pour datetime-local input
  const convertGMTToLocalDateTimeString = (gmtDateString: string): string => {
    const date = new Date(gmtDateString);
    // Extraire les composants de date/heure locaux
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const { register, handleSubmit, formState: { errors }, setValue, watch } = useForm<CreateRendezVousData>({
    defaultValues: rendezVous ? {
      patient: rendezVous.patient,
      doctor: rendezVous.doctor,
      date_heure: convertGMTToLocalDateTimeString(rendezVous.date_heure),
      motif: rendezVous.motif,
      notes: rendezVous.notes || '',
      duree: rendezVous.duree || '01:00:00'
    } : {
      patient: navigationState?.selectedPatientId || undefined,
      duree: '01:00:00'
    }
  });

  // Effet pour définir le patient pré-sélectionné
  useEffect(() => {
    if (navigationState?.selectedPatientId && !rendezVous) {
      setValue('patient', navigationState.selectedPatientId);
    }
  }, [navigationState, setValue, rendezVous]);

  // Récupération des patients avec recherche
  const { data: patientsResponse } = useQuery<PaginatedResponse<Patient>>({
    queryKey: ['patients', patientSearchTerm],
    queryFn: () => patientService.getAll({ 
      search: patientSearchTerm || undefined,
      page_size: 50 
    }).then(res => res.data),
    keepPreviousData: true
  });
  const patients = patientsResponse?.results || [];

  // Filtrer les patients selon le terme de recherche
  const filteredPatients = patients.filter(patient => 
    `${patient.nom} ${patient.prenoms}`.toLowerCase().includes(patientSearchTerm.toLowerCase()) ||
    patient.telephone.includes(patientSearchTerm)
  );

  const selectedPatientId = watch('patient');
  const selectedPatient = patients.find(p => p.id === parseInt(selectedPatientId?.toString() || '0'));

  // Effet pour initialiser le terme de recherche
  useEffect(() => {
    if (selectedPatient) {
      setPatientSearchTerm(`${selectedPatient.nom} ${selectedPatient.prenoms}`);
    }
  }, [selectedPatient]);

  // Fermer le dropdown au clic à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.patient-search-container')) {
        setShowPatientDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Récupération des médecins
  const { data: doctors = [] } = useQuery<Doctor[]>({
    queryKey: ['doctors'],
    queryFn: () => planningService.getDoctors().then(res => res.data)
  });

  // Mutation pour créer un patient
  const createPatientMutation = useMutation<Patient, Error, CreatePatientData>({
    mutationFn: (data) => patientService.create(data).then(res => res.data),
    onSuccess: (newPatient) => {
      queryClient.invalidateQueries({ queryKey: ['patients'] });
      setValue('patient', newPatient.id);
      setShowNewPatientForm(false);
      setNewPatientData({ nom: '', prenoms: '', telephone: '', genre: 'M', age_input: '' });
    }
  });

  // Mutation pour créer/modifier un rendez-vous
  const rdvMutation = useMutation<RendezVous, Error, CreateRendezVousData>({
    mutationFn: (data) => rendezVous
      ? planningService.updateRendezVous(rendezVous.id, data).then(res => res.data)
      : planningService.createRendezVous(data).then(res => res.data),
    onSuccess: () => {
      onSuccess?.();
    }
  });

  const handleNewPatientSubmit = async () => {
    if (!newPatientData.nom || !newPatientData.prenoms || !newPatientData.telephone || !newPatientData.age_input) {
      alert('Veuillez remplir tous les champs requis');
      return;
    }
    const erreurTelephone = validerNumero(newPatientData.telephone);
    if (erreurTelephone !== true) {
      alert(erreurTelephone);
      return;
    }
    
    try {
      await createPatientMutation.mutateAsync({
        ...newPatientData,
        age_input: Number(newPatientData.age_input)
      });
    } catch (error) {
      console.error('Erreur lors de la création du patient:', error);
    }
  };

  const handleNewPatientChange = (field: string, value: string) => {
    setNewPatientData(prev => ({ ...prev, [field]: value }));
  };

  const handlePatientSelect = (patient: Patient) => {
    setValue('patient', patient.id);
    setPatientSearchTerm(`${patient.nom} ${patient.prenoms}`);
    setShowPatientDropdown(false);
  };

  const handleSearchInputChange = (value: string) => {
    setPatientSearchTerm(value);
    setShowPatientDropdown(true);
    if (!value) {
      setValue('patient', '' as any);
    }
  };

  const onSubmit: SubmitHandler<CreateRendezVousData> = async (data) => {
    try {
      // Convertir la date/heure locale en format ISO (GMT)
      const localDate = new Date(data.date_heure);
      const isoDateString = localDate.toISOString();

      const submitData = {
        ...data,
        date_heure: isoDateString
      };

      await rdvMutation.mutateAsync(submitData);
    } catch (error: any) {
      console.error('Erreur lors de la sauvegarde:', error);
      // Ex. créneau réservé entre-temps via WhatsApp (un RDV actif par praticien et par heure)
      alert(error?.response?.data?.date_heure?.[0] ?? "Erreur lors de l'enregistrement du rendez-vous");
    }
  };

  return (
    <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-md p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">
          {rendezVous ? 'Modifier le rendez-vous' : 'Nouveau rendez-vous'}
        </h2>
        <button
          onClick={onClose}
          className="text-gray-500 hover:text-gray-700"
          title="Fermer le formulaire"
          aria-label="Fermer le formulaire"
        >
          <X className="h-6 w-6" />
        </button>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Patient *
              </label>
              <button
                type="button"
                onClick={() => setShowNewPatientForm(!showNewPatientForm)}
                className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100"
              >
                <Plus className="w-3 h-3 mr-1" />
                Nouveau patient
              </button>
            </div>
            
            {!showNewPatientForm ? (
              <div className="relative patient-search-container">
                <input
                  type="text"
                  placeholder="Rechercher un patient (nom, prénom, téléphone)..."
                  value={patientSearchTerm}
                  onChange={(e) => handleSearchInputChange(e.target.value)}
                  onFocus={() => setShowPatientDropdown(true)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-900"
                />
                
                {/* Hidden input for form validation */}
                <input
                  type="hidden"
                  {...register('patient', { required: 'Le patient est requis' })}
                />
                
                {/* Dropdown avec les résultats de recherche */}
                {showPatientDropdown && patientSearchTerm && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                    {filteredPatients.length > 0 ? (
                      filteredPatients.slice(0, 10).map(patient => (
                        <button
                          key={patient.id}
                          type="button"
                          onClick={() => handlePatientSelect(patient)}
                          className="w-full text-left px-3 py-2 hover:bg-blue-50 focus:bg-blue-50 focus:outline-none border-b border-gray-100 last:border-b-0"
                        >
                          <div className="font-medium text-gray-900">
                            {patient.nom} {patient.prenoms}
                          </div>
                          <div className="text-sm text-gray-600">
                            {patient.telephone} • {patient.age} ans
                          </div>
                        </button>
                      ))
                    ) : (
                      <div className="px-3 py-2 text-gray-500 text-sm">
                        Aucun patient trouvé
                      </div>
                    )}
                  </div>
                )}
                
                {errors.patient && <p className="text-red-500 text-sm mt-1">{errors.patient.message}</p>}
              </div>
            ) : (
              <div className="border border-gray-200 rounded-md p-4 bg-gray-50 space-y-3">
                <div className="flex items-center mb-2">
                  <User className="w-4 h-4 mr-2 text-gray-500" />
                  <span className="text-sm font-medium text-gray-700">Créer un nouveau patient</span>
                </div>
                
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="text"
                    placeholder="Nom *"
                    value={newPatientData.nom}
                    onChange={(e) => handleNewPatientChange('nom', e.target.value)}
                    className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-teal-500 text-gray-900"
                  />
                  <input
                    type="text"
                    placeholder="Prénoms *"
                    value={newPatientData.prenoms}
                    onChange={(e) => handleNewPatientChange('prenoms', e.target.value)}
                    className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-teal-500 text-gray-900"
                  />
                </div>
                
                <PhoneInput
                  placeholder="Téléphone *"
                  value={newPatientData.telephone}
                  onChange={(valeur) => handleNewPatientChange('telephone', valeur)}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-teal-500 text-gray-900"
                />
                
                <div className="grid grid-cols-2 gap-2">
                  <label htmlFor="genre-select" className="sr-only">
                    Genre *
                  </label>
                  <select
                    id="genre-select"
                    value={newPatientData.genre}
                    onChange={(e) => handleNewPatientChange('genre', e.target.value)}
                    className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-teal-500 text-gray-900"
                  >
                    <option value="M">Masculin</option>
                    <option value="F">Féminin</option>
                  </select>
                  <input
                    type="number"
                    min={0}
                    max={120}
                    value={newPatientData.age_input}
                    onChange={(e) => handleNewPatientChange('age_input', e.target.value)}
                    className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-teal-500 text-gray-900"
                    placeholder="Âge (ans) *"
                  />
                </div>
                
                <div className="flex justify-end space-x-2 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowNewPatientForm(false)}
                    className="px-3 py-1 text-xs text-gray-600 bg-gray-200 rounded hover:bg-gray-300"
                  >
                    Annuler
                  </button>
                  <button
                    type="button"
                    onClick={handleNewPatientSubmit}
                    disabled={createPatientMutation.isLoading}
                    className="px-3 py-1 text-xs text-white bg-teal-600 rounded hover:bg-teal-700 disabled:opacity-50"
                  >
                    {createPatientMutation.isLoading ? 'Création...' : 'Créer'}
                  </button>
                </div>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Médecin *
            </label>
            <select
              {...register('doctor', { required: 'Le médecin est requis' })}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-900"
            >
              <option value="">Sélectionner un médecin</option>
              {doctors.map(doctor => (
                <option key={doctor.id} value={doctor.id}>
                  {doctor.user_details ? (
                    `Dr. ${doctor.user_details.first_name} ${doctor.user_details.last_name}`
                  ) : (
                    doctor.user ? (
                      `Dr. ${doctor.user.first_name} ${doctor.user.last_name}`
                    ) : `Médecin #${doctor.id}`
                  )}
                </option>
              ))}
            </select>
            {errors.doctor && <p className="text-red-500 text-sm mt-1">{errors.doctor.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date et heure *
            </label>
            <input
              type="datetime-local"
              {...register('date_heure', { required: 'La date et heure sont requises' })}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-900"
            />
            {errors.date_heure && <p className="text-red-500 text-sm mt-1">{errors.date_heure.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Durée
            </label>
            <select
              {...register('duree')}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-900"
            >
              <option value="00:30:00">30 minutes</option>
              <option value="01:00:00">1 heure</option>
              <option value="01:30:00">1h 30</option>
              <option value="02:00:00">2 heures</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Motif *
          </label>
          <input
            {...register('motif', { required: 'Le motif est requis' })}
          className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-900"
            placeholder="Ex: Consultation, Détartrage, Soins..."
          />
          {errors.motif && <p className="text-red-500 text-sm mt-1">{errors.motif.message}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Notes
          </label>
          <textarea
            {...register('notes')}
            rows={3}
          className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-900"
            placeholder="Notes complémentaires..."
          />
        </div>

        {/* Boutons d'action */}
        <div className="flex justify-end space-x-4 pt-6 border-t">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={rdvMutation.isLoading}
            className="px-6 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 disabled:opacity-50"
          >
            {rdvMutation.isLoading ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default RendezVousForm;